<script>
import Layout from '@layouts/adminLayout.vue'
import FormItemGroup from '@components/form-item-group.vue'
import FormItem from '@components/form-item.vue'
import InfoBlock from '@components/info-block.vue'
import MottoIcon from '@components/motto-icon.vue'
import TagSelector from '@components/tag-selector.vue'
import LoaderIcon from '@components/loader-icon.vue'
import GrayHeader from '@components/gray-header.vue'
import MottoBreadcrumbs from '@components/motto-breadcrumbs.vue'
import SearchRecipientResult from '@components/network/search-recipient-result.vue'
import { states } from '@utils/consts/states'
import router from '@router'
import {
  postReferral,
  resendReferral,
  getLosByState,
  getOfficesByState,
  getMySentReferral,
} from '@src/api/networkConnect'
import { parseErrorMessage } from '@utils/string-helpers'
import _ from 'lodash'
import { authComputed } from '@state/helpers'
import { saveDataTracking } from '@src/api/tracking'

export default {
  page: {
    title: 'Network Connect Create Referral',
    meta: [
      {
        name: 'description',
        content: 'The Network Connect Create Referral page.',
      },
    ],
  },
  components: {
    Layout,
    FormItemGroup,
    FormItem,
    GrayHeader,
    MottoBreadcrumbs,
    InfoBlock,
    TagSelector,
    SearchRecipientResult,
    LoaderIcon,
    MottoIcon,
  },
  data() {
    return {
      isResendPage: false,
      resendReferralId: null,
      reviewMode: false,
      loading: true,
      error: false,
      submitting: false,
      searchBy: null,
      gettingRandomizedRecipients: false,
      searchSeed: null,
      searchQuery: null,
      searchingRecipients: false,
      searchRecipients: [],
      recipients: [],
      selectedRecipients: [],
      selectedRecipientIds: [],
      loadedRandomizedRecipientPages: 0,
      lastRandomizedRecipientsPage: null,
      states: states,
      loanTypeOptions: ['Conventional', 'FHA', 'HELOC', 'USDA Rural', 'VA', 'Other'],
      loanAmountOptions: ['0 - 100,000', '100,001 - 250,000', '250,001 - 500,000', '500,001 - 750,000', '750,001+'],
      state: null,
      referralType: '',
      selectAllUsers: [],
      borrowerFirstName: null,
      borrowerLastName: null,
      loanType: null,
      loanAmount: null,
    }
  },
  computed: {
    ...authComputed,
    recipientsLabel() {
      if (this.reviewMode && !this.isStatewide) {
        if (this.selectedRecipientIds.length === 1) {
          return `1 recipient`
        }

        return `${this.selectedRecipientIds.length} recipients`
      }
      return 'Recipients'
    },
    searchLabel() {
      return this.referralType === 'Users' ? `Search users by name, tradename or email` : `Search by city or zipcode`
    },
    isLastPageLoaded() {
      return (
        this.lastRandomizedRecipientsPage !== null &&
        this.loadedRandomizedRecipientPages >= this.lastRandomizedRecipientsPage
      )
    },
    isStatewide() {
      return this.referralType === 'Users' && this.selectAllUsers[0]
    },
  },
  watch: {
    referralType() {
      if (this.referralType === 'Users' || this.referralType === 'Offices') {
        this.searchBy = this.referralType
        this.resetRandomizedRecipients()
        this.searchRecipients = []
        this.selectAllUsers = []
        this.getRandomizedRecipients()
        return
      }

      this.searchBy = null
    },
    state() {
      if (this.referralType === 'Users' || this.referralType === 'Offices') {
        this.resetRandomizedRecipients()
        this.selectAllUsers = []
        this.getRandomizedRecipients()
      }
    },
    selectAllUsers() {
      if (this.selectAllUsers[0]) {
        this.searchQuery = ''
        this.selectedRecipientIds = []
        this.selectedRecipients = []
      }
    },
  },
  created() {
    if (!this.canManageNetworkConnect) {
      router.push({ name: 'home' })
    }
    this.isResendPage = this.$route.name === 'network-connect-resend-referral'
    this.resendReferralId = this.$route.params?.id
    if (this.isResendPage) {
      this.loadResendReferral()
    } else {
      this.loading = false
    }
  },
  methods: {
    async loadResendReferral() {
      try {
        this.loading = true
        const { data: referral } = await getMySentReferral({ id: this.$route.params.id })
        if (referral.status !== 'Expired' && referral.status !== 'Declined') {
          this.error = true
          this.loading = false
        }
        this.resendReferralId = referral.id
        this.borrowerLastName = referral.borrowerLastName
        this.borrowerFirstName = referral.borrowerFirstName
        this.state = referral.state
        this.loanType = referral.loanType
        this.loanAmount = referral.loanAmount
        this.loading = false
      } catch (err) {
        this.error = true
        this.loading = false
      }
    },
    async submitForm() {
      if (this.submitting) {
        return
      }

      this.submitting = true

      let referralDetails = {
        state: this.state,
        borrowerFirstName: this.borrowerFirstName,
        borrowerLastName: this.borrowerLastName,
        loanType: this.loanType,
        loanAmount: this.loanAmount,
        referralType: this.selectAllUsers[0] ? 'State-wide' : this.referralType,
      }

      if (this.referralType === 'Users') {
        referralDetails = {
          ...referralDetails,
          users: this.selectedRecipientIds.map((id) => ({ id })),
        }
      }

      if (this.referralType === 'Offices') {
        referralDetails = {
          ...referralDetails,
          offices: this.selectedRecipientIds.map((id) => ({ id })),
        }
      }

      referralDetails = _.pickBy(referralDetails, _.identity)

      try {
        const confirmationMsg = this.isStatewide
          ? `Are you sure you want to send this referral to all licensed MLOs in ${this.state}?`
          : `Are you sure you want to send this referral to ${this.selectedRecipientIds.length} recipient(s)?`
        await this.$bvModal
          .msgBoxConfirm(confirmationMsg, {
            modalClass: 'confirmation-modal',
            title: 'Confirm',
            okTitle: 'Yes',
            centered: true,
          })
          .then(async (confirmation) => {
            if (confirmation) {
              let trackIdentifier = 'REFERRAL - SENT'
              if (this.isResendPage) {
                await resendReferral({ referralDetails }, this.resendReferralId)
                trackIdentifier = 'REFERRAL - RESEND'
              } else {
                await postReferral({ referralDetails })
              }
              await saveDataTracking(trackIdentifier)
              this.$root.$bvToast.toast('Referral sent successfully.', {
                toaster: 'b-toaster-bottom-center',
                toastClass: 'toast-success',
              })

              await router.push({
                name: 'network-connect',
                params: {
                  selectedTab: 'sent',
                },
              })
            }
          })
      } catch (err) {
        this.$bvToast.toast(parseErrorMessage(err), {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
      } finally {
        this.submitting = false
      }
    },
    enterReviewMode() {
      if (!this.borrowerFirstName && !this.borrowerLastName) {
        this.$bvToast.toast('Either Borrower First or Last Name is required', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
        this.submitting = false
        return
      }

      if (!this.isStatewide && !this.selectedRecipientIds.length) {
        this.$bvToast.toast('Select at least one recipient', {
          toaster: 'b-toaster-bottom-center',
          toastClass: 'toast-error',
        })
        this.submitting = false
        return
      }

      window.scrollTo(0, 0)
      this.reviewMode = true
    },

    resetRandomizedRecipients() {
      this.gettingRandomizedRecipients = false
      this.searchSeed = null
      this.searchQuery = null
      this.selectedRecipients = []
      this.selectedRecipientIds = []
      this.recipients = []
      this.lastRandomizedRecipientsPage = null
      this.loadedRandomizedRecipientPages = 0
    },

    async getRandomizedRecipients() {
      if (this.gettingRandomizedRecipients) {
        return
      }

      if (this.isLastPageLoaded) {
        return
      }

      if (!this.state) {
        return
      }

      try {
        if (this.searchAbortController) {
          this.searchAbortController.abort()
        }
        this.searchAbortController = new AbortController()
        this.searchAbortSignal = this.searchAbortController.signal
        const referralType = this.referralType
        this.gettingRandomizedRecipients = true
        const searchOptions = {
          state: this.state,
          seed: this.searchSeed,
          page: this.loadedRandomizedRecipientPages + 1,
          referralId: this.resendReferralId,
          signal: this.searchAbortSignal,
        }
        const {
          data: { seedOrder, currentPage, lastPage, data: recipients },
        } = referralType === 'Users' ? await getLosByState(searchOptions) : await getOfficesByState(searchOptions)
        const recipientType = referralType === 'Users' ? 'user' : 'office'
        this.recipients = [
          ...this.recipients,
          ...recipients.map((recipient) => ({
            ...recipient,
            recipientType: recipientType,
          })),
        ].filter((recipient) => recipient.recipientType === recipientType)
        this.loadedRandomizedRecipientPages = currentPage
        this.lastRandomizedRecipientsPage = lastPage
        this.searchSeed = seedOrder
      } catch (e) {
      } finally {
        this.gettingRandomizedRecipients = false
      }
    },

    searchRecipientsBySearchQuery: _.debounce(async function () {
      if (!this.searchQuery) {
        this.searchingRecipients = false
        return
      }
      const referralType = this.referralType

      this.searchRecipients = []

      try {
        this.searchingRecipients = true
        const searchOptions = {
          state: this.state,
          search: this.searchQuery,
          referralId: this.resendReferralId,
          signal: this.searchAbortSignal,
        }
        const {
          data: { data: recipients },
        } = referralType === 'Users' ? await getLosByState(searchOptions) : await getOfficesByState(searchOptions)
        this.searchRecipients = [
          ...recipients.map((recipient) => ({
            ...recipient,
            recipientType: referralType === 'Users' ? 'user' : 'office',
          })),
        ]
      } catch (e) {
      } finally {
        this.searchingRecipients = false
      }
    }, 300),

    selectRecipient({ recipientId }) {
      if (this.selectedRecipientIds.includes(recipientId)) {
        this.selectedRecipientIds = this.selectedRecipientIds.filter((id) => id !== recipientId)
        this.selectedRecipients = this.selectedRecipients.filter((recipient) => recipient.id !== recipientId)
        return
      }

      this.selectedRecipientIds = [...this.selectedRecipientIds, recipientId]
      this.selectedRecipients = [...this.selectedRecipients, this.getRecipientById({ recipientId })]
    },

    getRecipientById({ recipientId }) {
      return (
        _.find(this.recipients, { id: recipientId }) ||
        _.find(this.searchRecipients, { id: recipientId }) ||
        _.find(this.selectedRecipients, { id: recipientId }) ||
        {}
      )
    },

    async confirmExit() {
      this.$bvModal
        .msgBoxConfirm(
          `Are you sure you want to cancel this action?
          All changes will be discarded.`,
          {
            modalClass: 'confirmation-modal',
            title: 'Cancel',
            okTitle: 'Continue',
            okVariant: 'danger',
            centered: true,
          }
        )
        .then((shouldExit) => {
          if (shouldExit) {
            router.push({
              name: 'network-connect',
              params: {
                selectedTab: 'sent',
              },
            })
          }
        })
    },
    checkScrollForSearchContainer({ target }) {
      if (target.scrollHeight - target.scrollTop - target.clientHeight < 100) {
        this.getRandomizedRecipients()
      }
    },

    shouldDisableRecipient(recipient) {
      const recipientOfficeId = recipient.recipientType === 'office' ? recipient.id : recipient.primaryOffice?.id
      const myOfficeId = this.currentUser.primaryOffice?.id

      return recipientOfficeId === myOfficeId
    },
  },
}
</script>

<template>
  <Layout>
    <LoaderIcon v-if="loading || error" :error="error" class="m-auto"></LoaderIcon>
    <div v-else>
      <GrayHeader>
        <MottoBreadcrumbs
          :items="[
            { label: 'Network Connect', routeName: 'network-connect' },
            {
              label: isResendPage ? 'Resend Referral' : `Create Referral`,
            },
          ]"
          :back-route="'network-connect'" />
        <BaseTitle class="gray-header-title">{{ isResendPage ? 'Resend Referral' : `Create Referral` }}</BaseTitle>
      </GrayHeader>
      <p :class="$style.borrowerText" class="mt-3 color-secondary-light-blue">
        Borrower information will NEVER be shared and is only displayed for you to organize your deal-level referrals.
        Either Borrower First or Last Name is required.
      </p>
      <ValidationObserver v-slot="{ handleSubmit }" ref="validationObserver">
        <FormItemGroup>
          <FormItem
            label="Borrower First Name"
            :edit-mode="!reviewMode && !isResendPage"
            :value.sync="borrowerFirstName"
            placeholder="Borrower First Name" />
          <FormItem
            label="Borrower Last Name"
            :edit-mode="!reviewMode && !isResendPage"
            :value.sync="borrowerLastName"
            placeholder="Borrower Last Name" />
          <FormItem
            label="Subject Property Address State"
            :edit-mode="!reviewMode && !isResendPage"
            :value.sync="state"
            :rules="'required'"
            :edit-select-values="states"
            placeholder="Select..." />
        </FormItemGroup>
        <FormItemGroup>
          <FormItem
            label="Loan Type"
            :edit-mode="!reviewMode && !isResendPage"
            :value.sync="loanType"
            :edit-select-values="loanTypeOptions"
            :rules="'required'"
            placeholder="Select..." />
          <FormItem
            label="Loan Amount"
            :edit-mode="!reviewMode && !isResendPage"
            :value.sync="loanAmount"
            :edit-select-values="loanAmountOptions"
            placeholder="Select..." />
          <div></div>
        </FormItemGroup>
        <p class="p1 mt-10 mb-10 color-dark-gray">{{ recipientsLabel }}:</p>
        <div v-if="!reviewMode" :class="$style.radioButtonsContainer">
          <BaseRadioButton v-model="referralType" label="Offices" name="referralTypeButton" value="Offices" />
          <BaseRadioButton v-model="referralType" label="MLOs" name="referralTypeButton" value="Users" />
        </div>
        <div v-if="searchBy && !reviewMode" :class="$style.searchContainer">
          <div :class="$style.searchInputContainer">
            <div class="d-flex position-relative">
              <BaseInputText
                v-model="searchQuery"
                :class="$style.searchQuery"
                :disabled="!state || selectAllUsers[0]"
                name="searchQuery"
                :placeholder="searchLabel"
                @input="
                  () => {
                    searchingRecipients = true
                    searchRecipientsBySearchQuery()
                  }
                " />
              <MottoIcon :class="$style.searchIcon" name="Search" />
            </div>
            <BaseCheckbox
              v-if="state && referralType === 'Users'"
              v-model="selectAllUsers"
              label="Select All (State Wide)"
              :input-value="'1'"></BaseCheckbox>
          </div>
          <div :class="$style.searchResultsContainer" @scroll="checkScrollForSearchContainer">
            <p v-if="!state" class="text-center my-3">Select a state to display recipients</p>
            <template v-if="searchRecipients.length && searchQuery && !searchingRecipients">
              <SearchRecipientResult
                v-for="recipient in searchRecipients"
                :key="recipient.id"
                :recipient="recipient"
                :disabled="shouldDisableRecipient(recipient)"
                :selected="selectedRecipientIds.includes(recipient.id)"
                @select-recipient="selectRecipient" />
            </template>
            <template v-else-if="recipients.length && !searchQuery">
              <SearchRecipientResult
                v-for="recipient in recipients"
                :key="recipient.id"
                :disabled="shouldDisableRecipient(recipient)"
                :recipient="recipient"
                :selected="selectedRecipientIds.includes(recipient.id) || !!selectAllUsers[0]"
                @select-recipient="selectRecipient" />
            </template>
            <p
              v-if="
                loadedRandomizedRecipientPages !== 0 && recipients.length === 0 && !searchQuery && !searchingRecipients
              "
              class="text-center my-3">
              No recipients found
            </p>
            <p v-if="!searchingRecipients && searchQuery && searchRecipients.length === 0" class="text-center my-3">
              No recipients found
            </p>
            <LoaderIcon v-if="searchingRecipients" class="my-3" />
            <LoaderIcon v-if="referralType && state && !isLastPageLoaded && !searchQuery" class="my-3" />
          </div>
        </div>
        <h4 v-if="isStatewide && reviewMode" class="font-weight-bold color-primary-blue">State-wide</h4>
        <div v-if="!isStatewide" :class="$style.selectedRecipientsTags">
          <TagSelector
            v-for="recipientId in selectedRecipientIds"
            :key="recipientId"
            :removable="!reviewMode"
            @remove="
              () => {
                selectRecipient({ recipientId })
              }
            ">
            {{ getRecipientById({ recipientId }).name || getRecipientById({ recipientId }).tradeName }}
          </TagSelector>
        </div>

        <InfoBlock v-if="reviewMode" :class="$style.respaWarning" theme="warning" hide-icon class="mt-5">
          Disclaimer: Nothing of value can be received or given for this referral per Respa section 8
        </InfoBlock>
        <div v-if="!reviewMode" :class="$style.bottomButtonsContainer">
          <BaseButton :options="{ defaultPadding: true }" theme="white" @click="confirmExit">Cancel</BaseButton>
          <BaseButton
            :options="{ defaultPadding: true }"
            :disabled="!referralType"
            @click="handleSubmit(enterReviewMode)">
            Review
          </BaseButton>
        </div>
        <div v-if="reviewMode" :class="$style.bottomButtonsContainer">
          <BaseButton :options="{ defaultPadding: true }" theme="white" @click="reviewMode = false">Edit</BaseButton>
          <BaseButton :options="{ defaultPadding: true }" :disabled="!referralType" @click="submitForm">
            <template v-if="submitting">
              <BaseIcon name="spinner" spin />
            </template>
            <template v-else>Submit</template>
          </BaseButton>
        </div>
      </ValidationObserver>
    </div>
  </Layout>
</template>

<style lang="scss" module>
@import '@design';

.bottomButtonsContainer {
  @media (max-width: $lg-breakpoint) {
    margin-top: 32px;
  }

  display: flex;
  justify-content: center;
  margin-top: 40px;

  button:first-child {
    margin-right: 24px;
  }
}

.radioButtonsContainer {
  @media (max-width: $lg-breakpoint) {
    flex-direction: column;
  }

  display: flex;

  > div {
    @media (max-width: $lg-breakpoint) {
      margin-right: 0;
      margin-bottom: 10px;
    }

    margin-right: 10px;
  }
}

.searchContainer {
  margin-top: 30px;
}

.searchResultsContainer {
  max-height: 300px;
  padding: 10px;
  margin-bottom: 20px;
  overflow-y: scroll;
  border: 1px solid $gray;
  border-radius: 8px;

  > div {
    margin-bottom: 10px;
  }
}

.selectedRecipientsTags {
  display: flex;
  flex-wrap: wrap;
  min-height: 43px;

  > div {
    margin-right: 6px;
    margin-bottom: 6px;
  }
}

.searchInputContainer {
  @media (max-width: $lg-breakpoint) {
    display: block;
  }

  display: flex;
  width: 100%;

  > div:first-child {
    @media (max-width: $lg-breakpoint) {
      width: 100%;
    }

    width: 50%;
  }

  > div:nth-child(2) {
    margin-top: 8px;
    margin-left: 10px;
  }

  input[type='checkbox'] {
    @media (max-width: $lg-breakpoint) {
      margin-bottom: 20px;
    }
  }
}

.searchQuery {
  @media (max-width: $lg-breakpoint) {
    width: 100%;
  }

  width: 100%;
  margin-bottom: 20px;
}

.searchIcon {
  margin: 8px 0 auto -40px;
}

.respaWarning {
  p {
    font-size: 14px;
    text-align: center;
  }
}

.borrowerText {
  margin-top: -15px;
  margin-bottom: 10px;
}
</style>
